import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useSceneTheme } from '@/lib/hooks/useSceneTheme';
import { RootState } from '@/store';

interface UseVideoTransitionOptions {
  /** 是否为待机模式 */
  entryStandbyMode?: boolean;
  /** 转场动画持续时间（毫秒） */
  transitionDuration?: number;
}

interface UseVideoTransitionReturn {
  /** 目标视频源 */
  targetVideoSrc: string | null;
  /** 是否可以开始播放 */
  canStart: boolean;
  /** 是否已播放问候视频 */
  greetPlayed: boolean;
  /** 设置问候视频播放状态 */
  setGreetPlayed: (played: boolean) => void;
}

/**
 * 视频切换逻辑Hook
 * 管理视频源的选择和切换逻辑
 */
export function useVideoTransition({
  entryStandbyMode = false,
  transitionDuration = 300,
}: UseVideoTransitionOptions = {}): UseVideoTransitionReturn {
  const room = useSelector((state: RootState) => state.room);
  const { isAITalking, isUserTalking } = room;
  const [greetPlayed, setGreetPlayed] = useState(false);

  // 获取主题视频配置
  const { videos } = useSceneTheme();
  const greetVideo = videos?.greet || null;
  const talkVideo = videos?.talk || null;
  const listenVideo = videos?.listen || null;
  const thinkVideo = videos?.think || null;
  const defaultVideo = videos?.default || null;

  // 计算目标视频源
  const targetVideoSrc = useCallback((): string | null => {
    // 待机模式下的视频选择逻辑
    if (entryStandbyMode) {
      if (!greetPlayed && greetVideo) {
        return greetVideo;
      }
      return defaultVideo || null;
    }

    // 聊天模式下的视频选择逻辑
    if (isAITalking && talkVideo) {
      return talkVideo;
    }

    if (isUserTalking && listenVideo) {
      return listenVideo;
    }

    // 默认状态 - 可以添加思考状态的判断
    if (thinkVideo) {
      return thinkVideo;
    }

    return defaultVideo || null;
  }, [
    entryStandbyMode,
    greetPlayed,
    isAITalking,
    isUserTalking,
    greetVideo,
    talkVideo,
    listenVideo,
    thinkVideo,
    defaultVideo,
  ])();

  // 判断是否可以开始播放
  const canStart = Boolean(targetVideoSrc);

  // 待机模式变化时重置问候状态
  useEffect(() => {
    if (entryStandbyMode) {
      setGreetPlayed(false);
    }
  }, [entryStandbyMode]);

  // 问候视频播放完成后的处理
  useEffect(() => {
    if (entryStandbyMode && !greetPlayed && targetVideoSrc === greetVideo) {
      // 设置一个定时器来标记问候视频播放完成
      // 这里可以根据实际视频长度来调整时间
      const timer = setTimeout(() => {
        setGreetPlayed(true);
      }, 3000); // 假设问候视频3秒

      return () => clearTimeout(timer);
    }
  }, [entryStandbyMode, greetPlayed, targetVideoSrc, greetVideo]);

  return {
    targetVideoSrc,
    canStart,
    greetPlayed,
    setGreetPlayed,
  };
}

export type { UseVideoTransitionOptions, UseVideoTransitionReturn };
